/**
 * Server-Sent Events over `fetch`. The browser's own EventSource cannot send headers, so a token would have to travel
 * in the URL (history, proxy logs, Referer); here it goes in the Authorization header like every other call.
 *
 * It behaves like EventSource where that matters: it opens a stream, hands over each event, tells when the stream is
 * up or down, and opens it again after a pause when the connection drops or the server hiccups (5xx). A refusal
 * (any other status) is final, like EventSource's.
 */

export interface StreamHandlers {
  /** One event with its name and the raw data text. */
  readonly onEvent: (type: string, data: string) => void;
  /** The stream is open (the server answered 200). */
  readonly onOpen: () => void;
  /** The stream ended or could not be opened; it will be tried again unless `final`. */
  readonly onDown: (final: boolean) => void;
}

/** The pause before opening again: 3 s, doubling up to 30 s while it keeps failing, spread by chance so pages do not all return at once. */
const RETRY_MS = 3000;
const RETRY_MAX_MS = 30_000;
/** An event that is still incomplete after this much text is not an event: the stream is dropped and opened again. */
const MAX_PENDING_CHARS = 1024 * 1024;

/** Pulls complete events out of the text received so far; returns what is left over. */
export function parseEvents(buffer: string, emit: (type: string, data: string) => void): string {
  // a CR at the very end may be the first half of a CRLF split over two reads: it waits for the next one
  const text = buffer.replace(/\r\n/g, '\n').replace(/\r(?!$)/g, '\n');
  const blocks = text.split('\n\n');
  const rest = blocks.pop() ?? '';
  for (const block of blocks) {
    let type = 'message';
    const data: string[] = [];
    for (const line of block.split('\n')) {
      if (line === '' || line.startsWith(':')) continue;
      const colon = line.indexOf(':');
      const field = colon === -1 ? line : line.slice(0, colon);
      const value = colon === -1 ? '' : line.slice(colon + 1).replace(/^ /, '');
      if (field === 'event') type = value;
      else if (field === 'data') data.push(value);
    }
    if (data.length > 0) emit(type, data.join('\n'));
  }
  return rest;
}

/** Opens the stream and keeps it open. `headers` is read again on every attempt. Returns a function that stops it. */
export function openStream(url: string, headers: () => Record<string, string>, handlers: StreamHandlers): () => void {
  let stopped = false;
  let controller: AbortController | null = null;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let failures = 0;

  const run = async (): Promise<void> => {
    controller = new AbortController();
    let final = false;
    try {
      const response = await fetch(url, { headers: { ...headers(), accept: 'text/event-stream' }, cache: 'no-store', signal: controller.signal });
      if (!response.ok || response.body === null) {
        final = response.status < 500 && response.status !== 429;
      } else {
        failures = 0;
        handlers.onOpen();
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        for (;;) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer = parseEvents(buffer + decoder.decode(value, { stream: true }), handlers.onEvent);
          if (buffer.length > MAX_PENDING_CHARS) {
            // drop the connection itself, not just this read: the retry must not leave this one open
            controller.abort();
            break;
          }
        }
      }
    } catch {
      // dropped or aborted: handled below
    }
    if (stopped) return;
    handlers.onDown(final);
    if (final) return;
    failures += 1;
    const pause = Math.min(RETRY_MAX_MS, RETRY_MS * 2 ** (failures - 1)) * (0.5 + Math.random());
    timer = setTimeout(() => void run(), pause);
  };
  void run();

  return () => {
    stopped = true;
    clearTimeout(timer);
    controller?.abort();
  };
}
